export const commandDefs = [
  {
    name: 'zoom',
    description: 'editor.command.zoom.description',
    args: ['25', '50', '75', '100', '150', '200']
  },
  {
    name: 'mode',
    description: 'editor.command.mode.description',
    args: ['default', 'inspect', 'panning', 'interact']
  },
  { name: 'editor', description: 'editor.command.editor.description', args: null },
  { name: 'settings', description: 'editor.command.settings.description', args: null },
  {
    name: 'go',
    description: 'editor.command.go.description',
    args: ['dashboard', 'canvas', 'settings']
  },
  { name: 'open', description: 'editor.command.open.description', args: 'dynamic' },
  { name: 'select', description: 'editor.command.select.description', args: 'dynamic' },
  { name: 'search', description: 'editor.command.search.description', args: null },
  { name: 'docs', description: 'editor.command.docs.description', args: null },
  { name: 'versions', description: 'editor.command.versions.description', args: null },
  { name: 'theme', description: 'editor.command.theme.description', args: ['dark', 'light'] },
  // Canvas v2 commands.
  {
    name: 'auto-layout',
    description: 'editor.command.autoLayout.description',
    args: null
  },
  { name: 'fit', description: 'editor.command.fit.description', args: null },
  { name: 'reset', description: 'editor.command.reset.description', args: null },
  { name: 'undo', description: 'editor.command.undo.description', args: null },
  { name: 'redo', description: 'editor.command.redo.description', args: null },
  { name: 'duplicate', description: 'editor.command.duplicate.description', args: null },
  { name: 'delete', description: 'editor.command.delete.description', args: null },
  { name: 'group', description: 'editor.command.group.description', args: null },
  { name: 'ungroup', description: 'editor.command.ungroup.description', args: null },
  {
    name: 'snap',
    description: 'editor.command.snap.description',
    args: ['on', 'off', 'grid', 'items', 'guides']
  },
  {
    name: 'grid-step',
    description: 'editor.command.gridStep.description',
    args: 'dynamic'
  },
  {
    name: 'frame-mode',
    description: 'editor.command.frameMode.description',
    args: ['on', 'off']
  },
  { name: 'guides', description: 'editor.command.guides.description', args: ['clear'] },
  { name: 'shortcuts', description: 'editor.command.shortcuts.description', args: null },
  { name: 'help', description: 'editor.command.help.description', args: null },
  {
    name: 'align',
    description: 'editor.command.align.description',
    args: ['left', 'right', 'top', 'bottom', 'center', 'middle']
  },
  {
    name: 'distribute',
    description: 'editor.command.distribute.description',
    args: ['horizontal', 'vertical']
  }
]

// FA204 / frank trap — every command handler below reads its imperative
// canvas-DOM helpers (getGS/getIframeWindow/buildCanvasItemNodeMap/
// applyItemNodePatch) via `el.scope.<name>(...)`, NOT as bare module-scope
// calls: a bare module-scope const/function silently reads as undefined
// once CommandBar is frank.toJSON'd for platform delivery (workspace
// d6ea1142, project_frank_drops_module_local_helpers). `el` here is always
// the live CommandBar element instance (passed through from
// `CommandBar.scope.runCommand`'s `handler(bar, args)` call), so
// `el.scope` resolves to CommandBar's own re-eval'd scope regardless of
// serialization. `commandDefs` above and `commands` here stay top-level
// EXPORTS (unlike every other helper in this file) because CommandPalette.js
// imports both cross-file — real ES `import`/`export` bindings are NOT the
// same trap (out of scope per this ticket's rule 3) — and CommandBar.scope
// below holds its OWN reference to each (`{ commandDefs, commands, ... }`)
// so its internal helpers never depend on a bare same-file identifier.
export const commands = {
  zoom(el, args) {
    const value = parseFloat(args[0])
    if (!value || value <= 0) return el.call('polyglot', 'editor.command.usage.zoom')
    const gs = el.scope.getGS()
    if (!gs?.panzoomInstance) return el.call('polyglot', 'editor.command.result.noCanvasInstance')
    const win = el.scope.getIframeWindow()
    const { x, y } = gs.positions
    gs.panzoomInstance.smoothZoomAbs(x + win.innerWidth / 2, y + win.innerHeight / 2, value / 100)
    return el.call('polyglot', 'editor.command.result.zoomSet').replace('{value}', value)
  },

  mode(el, args) {
    const modes = ['defaultMode', 'inspectMode', 'panningMode', 'interactMode']
    const arg = (args[0] || '').toLowerCase()
    if (!arg) return el.call('polyglot', 'editor.command.usage.mode')
    // Accept short names ('interact' → 'interactMode'); existing chrome
    // speaks v1-style flags which methods/index.js bridges to v2.
    const resolved = modes.find((m) => m.toLowerCase().startsWith(arg))
    if (!resolved) return el.call('polyglot', 'editor.command.result.unknownMode')
    el.setCanvasMode(resolved)
    return el
      .call('polyglot', 'editor.command.result.modeSwitched')
      .replace('{mode}', resolved.replace('Mode', ''))
  },

  editor(el) {
    const cs = el.getCanvasScope()
    cs.editorActive = !cs.editorActive
    return el.call(
      'polyglot',
      cs.editorActive ? 'editor.command.result.editorOpened' : 'editor.command.result.editorClosed'
    )
  },

  settings(el) {
    el.call('openRouterModal', '/settings', { key: '/settings' })
    return el.call('polyglot', 'editor.command.result.settingsOpened')
  },

  versions(el) {
    // Registered by the canvas project (functions/versionHistory.js); on
    // surfaces without it el.call resolves to undefined and this is a no-op.
    el.call('toggleVersionHistory')
    return el.call('polyglot', 'editor.command.result.versionHistory')
  },

  go(el, args) {
    const path = args[0]
    if (!path) return el.call('polyglot', 'editor.command.usage.go')
    el.router(path, el.getRoot())
    return el.call('polyglot', 'editor.command.result.navigated').replace('{path}', path)
  },

  open(el, args) {
    const key = args[0]
    if (!key) return el.call('polyglot', 'editor.command.usage.open')
    // canvas's v1→v2 bridge (Rule 63 fix — was named the same as the real
    // shared setWindow primitive and shadowed it shell-wide; see
    // canvas/methods/index.js). Selects + fits the artboard in v2.
    el.call('bridgeSetWindow', 'explorer', key)
    return el.call('polyglot', 'editor.command.result.opened').replace('{key}', key)
  },

  select(el, args) {
    const key = args[0]
    if (!key) return el.call('polyglot', 'editor.command.usage.select')
    const rs = el.getRootState()
    const data = rs?.data
    const type = data?.pages?.[key] ? 'pages' : 'components'
    el.call('openArtboardItem', type, key)
    return el.call('polyglot', 'editor.command.result.selected').replace('{key}', key)
  },

  search(el, args) {
    const query = args.join(' ')
    if (!query) return el.call('polyglot', 'editor.command.usage.search')
    // canvas's v1→v2 bridge — see the /open comment above. No-op in v2 today
    // (the bridge explicitly skips `search:`-prefixed args).
    el.call('bridgeSetWindow', 'explorer', 'search:' + query)
    return el.call('polyglot', 'editor.command.result.searching').replace('{query}', query)
  },

  docs(el) {
    // canvas's v1→v2 bridge — see the /open comment above.
    el.call('bridgeSetWindow', 'explorer', 'onboarding')
    return el.call('polyglot', 'editor.command.result.docsOpened')
  },

  theme(el, args) {
    const target = args[0]
    if (target && target !== 'dark' && target !== 'light')
      return el.call('polyglot', 'editor.command.usage.theme')
    el.call('toggleTheme', target || undefined)
    return el.call('polyglot', 'editor.command.result.themeToggled')
  },

  // ── Canvas v2 commands ────────────────────────────────────────────
  'auto-layout': (el) => {
    el.call('autoLayout')
    return el.call('polyglot', 'editor.command.result.autoLaidOut')
  },
  fit: (el) => {
    el.call('fitToSelection')
    return el.call('polyglot', 'editor.command.result.fitToSelection')
  },
  reset: (el) => {
    el.call('resetCanvasView')
    return el.call('polyglot', 'editor.command.result.viewReset')
  },
  undo: (el) => {
    el.call('undo')
    return el.call('polyglot', 'editor.command.result.undone')
  },
  redo: (el) => {
    el.call('redo')
    return el.call('polyglot', 'editor.command.result.redone')
  },
  duplicate: (el) => {
    el.call('duplicateSelection')
    return el.call('polyglot', 'editor.command.result.duplicated')
  },
  delete: (el) => {
    el.call('deleteSelection')
    return el.call('polyglot', 'editor.command.result.deleted')
  },
  group: (el) => {
    el.call('group')
    return el.call('polyglot', 'editor.command.result.grouped')
  },
  ungroup: (el) => {
    const v2 = el.call('getCanvasV2')
    if (!v2) return el.call('polyglot', 'editor.command.result.noCanvas')
    const ids = v2.selection.ids
    Object.entries(v2.groups || {})
      .filter(([, g]) => g.children.some((c) => ids.includes(c)))
      .forEach(([gid]) => el.call('ungroup', gid))
    return el.call('polyglot', 'editor.command.result.ungrouped')
  },
  snap: (el, args) => {
    const v2 = el.call('getCanvasV2')
    if (!v2) return el.call('polyglot', 'editor.command.result.noCanvas')
    const a = (args[0] || '').toLowerCase()
    if (a === 'on') {
      el.call('patchSnap', { enabled: true })
      return el.call('polyglot', 'editor.command.result.snapOn')
    }
    if (a === 'off') {
      el.call('patchSnap', { enabled: false })
      return el.call('polyglot', 'editor.command.result.snapOff')
    }
    if (a === 'grid') {
      el.call('patchSnap', { toGrid: !v2.snap.toGrid })
      return el.call(
        'polyglot',
        !v2.snap.toGrid ? 'editor.command.result.gridSnapOn' : 'editor.command.result.gridSnapOff'
      )
    }
    if (a === 'items') {
      el.call('patchSnap', { toItems: !v2.snap.toItems })
      return el.call(
        'polyglot',
        !v2.snap.toItems ? 'editor.command.result.itemSnapOn' : 'editor.command.result.itemSnapOff'
      )
    }
    if (a === 'guides') {
      el.call('patchSnap', { toGuides: !v2.snap.toGuides })
      return el.call(
        'polyglot',
        !v2.snap.toGuides
          ? 'editor.command.result.guideSnapOn'
          : 'editor.command.result.guideSnapOff'
      )
    }
    return el.call('polyglot', 'editor.command.usage.snap')
  },
  'grid-step': (el, args) => {
    const n = Math.max(4, Math.min(500, Number(args[0]) || 0))
    if (!n) return el.call('polyglot', 'editor.command.usage.gridStep')
    el.call('patchSnap', { gridStep: n })
    return el.call('polyglot', 'editor.command.result.gridStep').replace('{n}', n)
  },
  guides: (el, args) => {
    if (args[0] === 'clear') {
      el.call('patchCanvas', { guides: { horizontal: [], vertical: [] } })
      return el.call('polyglot', 'editor.command.result.guidesCleared')
    }
    return el.call('polyglot', 'editor.command.usage.guides')
  },
  shortcuts: (el) => {
    el.call('openKeyboardShortcuts')
    return el.call('polyglot', 'editor.command.result.shortcutsOverlay')
  },
  help: (el) => {
    el.call('openKeyboardShortcuts')
    return el.call('polyglot', 'editor.command.result.shortcutsOverlay')
  },

  align: (el, args) => {
    const v2 = el.call('getCanvasV2')
    if (!v2) return el.call('polyglot', 'editor.command.result.noCanvas')
    const ids = v2.selection.ids
    if (ids.length < 2) return el.call('polyglot', 'editor.command.result.needTwo')
    const dir = (args[0] || '').toLowerCase()
    const items = v2.items
    const rects = ids.map((id) => items[id]).filter(Boolean)
    const nodeMap = el.scope.buildCanvasItemNodeMap()
    let target
    // The result key is chosen IN the branch, never assembled from `dir`
    // (docs/I18N-CONVENTION.md §1.3 — a concatenated key is invisible to the
    // parity gate and to grep).
    let resultKey
    if (dir === 'left') {
      const minX = Math.min(...rects.map((r) => r.x))
      target = () => ({ x: minX })
      resultKey = 'editor.command.result.alignedLeft'
    } else if (dir === 'right') {
      const maxRight = Math.max(...rects.map((r) => r.x + r.w))
      target = (r) => ({ x: maxRight - r.w })
      resultKey = 'editor.command.result.alignedRight'
    } else if (dir === 'top') {
      const minY = Math.min(...rects.map((r) => r.y))
      target = () => ({ y: minY })
      resultKey = 'editor.command.result.alignedTop'
    } else if (dir === 'bottom') {
      const maxBottom = Math.max(...rects.map((r) => r.y + r.h))
      target = (r) => ({ y: maxBottom - r.h })
      resultKey = 'editor.command.result.alignedBottom'
    } else if (dir === 'center') {
      const cx = (Math.min(...rects.map((r) => r.x)) + Math.max(...rects.map((r) => r.x + r.w))) / 2
      target = (r) => ({ x: cx - r.w / 2 })
      resultKey = 'editor.command.result.alignedCenter'
    } else if (dir === 'middle') {
      const cy = (Math.min(...rects.map((r) => r.y)) + Math.max(...rects.map((r) => r.y + r.h))) / 2
      target = (r) => ({ y: cy - r.h / 2 })
      resultKey = 'editor.command.result.alignedMiddle'
    } else return el.call('polyglot', 'editor.command.usage.align')
    ids.forEach((id) => {
      const r = items[id]
      if (!r) return
      const patch = target(r)
      el.call('patchCanvasItem', id, patch)
      el.scope.applyItemNodePatch(nodeMap.get(id), patch)
    })
    el.call('refreshCanvasChrome')
    el.call('pushHistory')
    el.call('persistCanvasLayout')
    return el.call('polyglot', resultKey)
  },

  distribute: (el, args) => {
    const v2 = el.call('getCanvasV2')
    if (!v2) return el.call('polyglot', 'editor.command.result.noCanvas')
    const ids = v2.selection.ids
    if (ids.length < 3) return el.call('polyglot', 'editor.command.result.needThree')
    const axisArg = (args[0] || '').toLowerCase()
    const axis = axisArg.startsWith('h') ? 'h' : axisArg.startsWith('v') ? 'v' : null
    if (!axis) return el.call('polyglot', 'editor.command.usage.distribute')
    const items = v2.items
    const sorted = [...ids]
      .filter((id) => items[id])
      .sort((a, b) => (axis === 'h' ? items[a].x - items[b].x : items[a].y - items[b].y))
    const first = items[sorted[0]]
    const last = items[sorted[sorted.length - 1]]
    const nodeMap = el.scope.buildCanvasItemNodeMap()
    const axisKey = axis === 'h' ? 'x' : 'y'
    const sizeKey = axis === 'h' ? 'w' : 'h'
    const total = last[axisKey] + last[sizeKey] - first[axisKey]
    const sum = sorted.reduce((acc, id) => acc + items[id][sizeKey], 0)
    const gap = (total - sum) / (sorted.length - 1)
    let cursor = first[axisKey]
    sorted.forEach((id) => {
      const r = items[id]
      const patch = { [axisKey]: cursor }
      el.call('patchCanvasItem', id, patch)
      el.scope.applyItemNodePatch(nodeMap.get(id), patch)
      cursor += r[sizeKey] + gap
    })
    el.call('refreshCanvasChrome')
    el.call('pushHistory')
    el.call('persistCanvasLayout')
    return el.call(
      'polyglot',
      axis === 'h' ? 'editor.command.result.distributedH' : 'editor.command.result.distributedV'
    )
  }
}

export const CommandBar = {
  extends: 'Navbar',
  // FA204 / frank trap — every helper below lives in `scope`, not module
  // scope: a bare module-scope const/function silently reads as undefined
  // once this component is frank.toJSON'd for platform delivery (workspace
  // d6ea1142, project_frank_drops_module_local_helpers). Called via
  // `el.scope.<name>(...)` (or `bar.scope.<name>(...)` — `bar` is always
  // the same live CommandBar element, just named for readability at each
  // call site) from any descendant factory/handler — including from
  // `commands` above, which is a SEPARATE top-level export (kept for
  // CommandPalette.js's cross-file `import { commandDefs, commands }`) but
  // always receives the real CommandBar element as its first arg, so
  // `el.scope` still resolves there too. Plain functions throughout
  // (never `this.<name>`) — every helper already takes `bar`/`el` as an
  // argument, so routing every cross-helper call through that argument's
  // `.scope` sidesteps the object-literal-siblings-can't-see-each-other
  // problem entirely (no method `this`-binding to keep straight across the
  // nested `commands` nesting below).
  scope: {
    // Mirror the top-level `commandDefs`/`commands` exports (declared above
    // this component) so this component's OWN internal helpers below never
    // depend on a bare same-file identifier post-serialization — only the
    // reference is duplicated, not the data (same object, same array).
    commandDefs,
    commands,

    getGS() {
      return window.__canvasGlobalScope
    },

    getIframeWindow() {
      const iframe = document.getElementById('canvas-iframe')
      return iframe?.contentWindow || window
    },

    // One DOM query per invocation — avoids N selector scans inside the
    // forEach loop below. patchCanvasItem is a non-reactive in-place
    // mutation, so we still need the imperative style write for immediate
    // visual feedback.
    buildCanvasItemNodeMap() {
      const map = new Map()
      document.querySelectorAll('[data-canvas-item]').forEach((n) => {
        map.set(n.getAttribute('data-canvas-item'), n)
      })
      return map
    },

    applyItemNodePatch(node, patch) {
      if (!node) return
      if (patch.x !== undefined) node.style.left = patch.x + 'px'
      if (patch.y !== undefined) node.style.top = patch.y + 'px'
    },

    runCommand(bar, input) {
      const value = (typeof input === 'string' ? input : bar.Input.node.value).trim()
      if (!value || !value.startsWith('/')) return
      const parts = value.slice(1).split(/\s+/)
      const cmd = parts[0].toLowerCase()
      const args = parts.slice(1)
      const handler = bar.scope.commands[cmd]
      const result = handler
        ? handler(bar, args)
        : bar.call('polyglot', 'editor.command.result.unknown').replace('{cmd}', cmd)

      bar.Input.node.value = ''
      bar.scope.hideDropdown(bar)
      bar.Input.node.blur()

      if (bar.data.feedbackTimer) clearTimeout(bar.data.feedbackTimer)
      if (bar.ResultToast?.node) {
        bar.ResultToast.node.textContent = result
        bar.ResultToast.node.style.opacity = '1'
        bar.data.feedbackTimer = setTimeout(() => {
          if (bar.ResultToast?.node) bar.ResultToast.node.style.opacity = '0'
        }, 3000)
      }
    },

    getDynamicArgs(bar, cmd) {
      const items = []
      try {
        const rs = bar.getRootState()
        const data = rs?.data
        if (!data) return items

        const categories = [
          { key: 'pages', label: 'editor.command.category.pages' },
          { key: 'components', label: 'editor.command.category.components' },
          { key: 'functions', label: 'editor.command.category.functions' }
        ]

        for (const cat of categories) {
          const obj = data[cat.key]
          if (!obj || typeof obj !== 'object') continue
          const keys = Object.keys(obj)
          if (!keys.length) continue
          items.push({ label: cat.label, type: 'header' })
          for (const k of keys) {
            items.push({
              label: k,
              description: cat.key.slice(0, -1),
              type: 'arg',
              fullValue: '/' + cmd + ' ' + k
            })
          }
        }
      } catch (e) {
        /* rootState may not be available */
      }
      return items
    },

    getFilteredItems(bar, value) {
      const raw = value || ''
      const trimmed = raw.trim()

      if (!trimmed || !trimmed.startsWith('/')) {
        return bar.scope.commandDefs.map((c) => ({
          label: '/' + c.name,
          description: bar.call('polyglot', c.description),
          type: 'command'
        }))
      }

      const parts = trimmed.slice(1).split(/\s+/)
      const cmd = parts[0].toLowerCase()
      const hasSpace = raw.includes(' ')

      if (hasSpace) {
        const def = bar.scope.commandDefs.find((c) => c.name === cmd)
        if (!def) return []

        if (def.args === 'dynamic') {
          const all = bar.scope.getDynamicArgs(bar, cmd)
          const argFilter = (parts[1] || '').toLowerCase()
          if (!argFilter) return all
          return all.filter((a) =>
            a.type === 'header' ? false : a.label.toLowerCase().includes(argFilter)
          )
        }

        if (!def.args)
          return [
            {
              label: '/' + cmd,
              description: bar
                .call('polyglot', 'editor.command.runPrefix')
                .replace('{command}', bar.call('polyglot', def.description).toLowerCase()),
              type: 'run',
              fullValue: '/' + cmd
            }
          ]
        const argFilter = (parts[1] || '').toLowerCase()
        return def.args
          .filter((a) => !argFilter || a.startsWith(argFilter))
          .map((a) => ({
            label: a,
            description: '',
            type: 'arg',
            fullValue: '/' + cmd + ' ' + a
          }))
      }

      return bar.scope.commandDefs
        .filter((c) => c.name.startsWith(cmd))
        .map((c) => ({
          label: '/' + c.name,
          description: bar.call('polyglot', c.description),
          type: 'command'
        }))
    },

    renderDropdown(bar, items, activeIndex) {
      const dropdown = bar.CmdDropdown?.node
      if (!dropdown) return
      dropdown.textContent = ''

      if (!items.length) {
        dropdown.style.opacity = '0'
        dropdown.style.visibility = 'hidden'
        return
      }

      let selectableIndex = 0
      items.forEach((item) => {
        if (item.type === 'header') {
          const header = document.createElement('div')
          header.style.cssText =
            'padding:4px 8px 2px;font-size:0.8em;opacity:0.35;text-transform:uppercase;' +
            'letter-spacing:0.05em;pointer-events:none;user-select:none;'
          header.textContent = item.label
          dropdown.appendChild(header)
          return
        }

        const idx = selectableIndex++
        const row = document.createElement('div')
        row.style.cssText =
          'display:flex;align-items:center;justify-content:space-between;' +
          'padding:4px 8px;cursor:pointer;border-radius:4px;gap:8px;' +
          (idx === activeIndex ? 'background:var(--color-selected);' : '')
        row.dataset.index = idx
        row.dataset.itemIndex = items.indexOf(item)

        const left = document.createElement('span')
        left.style.cssText = 'font-weight:500;white-space:nowrap;'
        left.textContent = item.label

        row.appendChild(left)

        if (item.description) {
          const right = document.createElement('span')
          right.style.cssText =
            'opacity:0.4;font-size:0.9em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;'
          right.textContent = item.description
          row.appendChild(right)
        }

        dropdown.appendChild(row)
      })
    },

    getSelectableItems(items) {
      return items.filter((i) => i.type !== 'header')
    },

    updateDropdown(bar) {
      const value = bar.Input?.node?.value || ''
      const items = bar.scope.getFilteredItems(bar, value)
      bar.data.filteredItems = items
      bar.data.selectableItems = bar.scope.getSelectableItems(items)
      bar.data.activeIndex = -1
      bar.scope.renderDropdown(bar, items, -1)
    },

    showDropdown(bar) {
      bar.scope.updateDropdown(bar)
      const dropdown = bar.CmdDropdown?.node
      if (dropdown) {
        dropdown.style.opacity = '1'
        dropdown.style.visibility = 'visible'
        dropdown.style.pointerEvents = 'auto'
      }
      bar.data.isOpen = true
    },

    hideDropdown(bar) {
      const dropdown = bar.CmdDropdown?.node
      if (dropdown) {
        dropdown.style.opacity = '0'
        dropdown.style.visibility = 'hidden'
        dropdown.style.pointerEvents = 'none'
      }
      bar.data.activeIndex = -1
      bar.data.isOpen = false
    }
  },
  position: 'relative',
  gap: '0',
  // A capsule, like every field-shaped control of the shell.
  round: 'radiusPill',
  userSelect: 'none',
  cursor: 'text',
  minWidth: 'G+C',
  padding: '0',

  data: {
    feedbackTimer: null,
    filteredItems: [],
    selectableItems: [],
    activeIndex: -1,
    isOpen: false
  },

  // FA503 / Rule 64. Both of this bar's listeners used to be armed
  // imperatively from `onRender` behind an `__cmdBarInit` latch:
  //   • the row-click delegation duplicated the declarative `onClick` below
  //     on the SAME node (two listeners, one element);
  //   • the outside-click `document.addEventListener('mousedown', …)` had NO
  //     removal at all — its own comment ("the listener outlives the
  //     element") described the leak instead of fixing it. Every route change
  //     that unmounted the bar left one more live document listener.
  // Both are now declared props: DOMQL registers each exactly once and drops
  // them in dispose().

  // Root click keeps the pre-fix ORDER: focus/open first (what the old
  // declarative `onClick` did), then the row delegation the imperative
  // listener added after it.
  onClick: (e, el) => {
    el.Input.node.focus()
    if (!el.data.isOpen) el.scope.showDropdown(el)

    const row = e.target.closest('[data-index]')
    if (!row) return
    e.stopPropagation()
    const itemIdx = parseInt(row.dataset.itemIndex)
    const item = el.data.filteredItems[itemIdx]
    if (!item) return
    if (item.type === 'run' || item.type === 'arg') {
      el.scope.runCommand(el, item.fullValue)
    } else {
      el.Input.node.value = item.label + ' '
      el.scope.showDropdown(el)
      el.Input.node.focus()
    }
  },

  onDocumentMousedown: (e, el) => {
    if (!el.node) return
    if (!el.node.contains(e.target)) {
      el.scope.hideDropdown(el)
      el.Input?.node?.blur?.()
    }
  },

  Icon: {
    name: 'chevronRight',
    width: 'A',
    height: 'A',
    margin: '- -B - -',
    opacity: '.35',
    flexShrink: '0',
    marginLeft: 'Z',
    pointerEvents: 'none'
  },

  Input: {
    tag: 'input',
    type: 'text',
    placeholder: (el) => el.call('polyglot', 'editor.command.placeholder'),
    theme: null,
    border: '0',
    outline: 'none',
    background: 'transparent',
    color: 'currentColor',
    fontFamily: 'inherit',
    width: '100%',
    padding: 'Z Z Z B2',
    flexGrow: '1',

    '::placeholder': {
      color: 'currentColor',
      opacity: '.25'
    },

    onInput: (e, el) => {
      const bar = el.parent
      bar.scope.updateDropdown(bar)
    },

    onKeyDown: (e, el) => {
      const bar = el.parent
      const selectable = bar.data.selectableItems || []
      let idx = bar.data.activeIndex

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (!bar.data.isOpen) {
          bar.scope.showDropdown(bar)
          return
        }
        idx = idx < selectable.length - 1 ? idx + 1 : 0
        bar.data.activeIndex = idx
        bar.scope.renderDropdown(bar, bar.data.filteredItems, idx)
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (!bar.data.isOpen) {
          bar.scope.showDropdown(bar)
          return
        }
        idx = idx > 0 ? idx - 1 : selectable.length - 1
        bar.data.activeIndex = idx
        bar.scope.renderDropdown(bar, bar.data.filteredItems, idx)
        return
      }

      if (e.key === 'Tab') {
        e.preventDefault()
        const item = selectable[idx >= 0 ? idx : 0]
        if (!item) return
        el.node.value = item.type === 'arg' ? item.fullValue : item.label + ' '
        bar.scope.updateDropdown(bar)
        return
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        if (idx >= 0 && selectable[idx]) {
          const item = selectable[idx]
          if (item.type === 'run') {
            bar.scope.runCommand(bar, item.fullValue)
          } else if (item.type === 'arg') {
            bar.scope.runCommand(bar, item.fullValue)
          } else {
            el.node.value = item.label + ' '
            bar.data.activeIndex = -1
            bar.scope.showDropdown(bar)
          }
          return
        }
        if (!el.node.value.trim()) return
        bar.scope.runCommand(bar, el.node.value)
      }

      if (e.key === 'Escape') {
        e.preventDefault()
        if (el.node.value) {
          el.node.value = ''
          bar.scope.updateDropdown(bar)
        } else {
          bar.scope.hideDropdown(bar)
          el.node.blur()
        }
      }
    }
  },

  CmdDropdown: {
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    marginTop: 'Y',
    flow: 'y',
    gap: 'W',
    padding: 'Z',
    // The popover recipe (DESIGN_SPEC §6.1): the shell glass, the card
    // radius, the overlay elevation (ring + layers once brand ships them).
    round: 'radiusCard',
    theme: 'canvas-card-glass',
    shadow: 'overlay',
    fontSize: 'Z2',
    // Adaptive (was fixed 'white') — canvas-card-glass already supplies its
    // own paired text color; the fixed override went invisible against the
    // theme's near-white light-mode glass background.
    color: 'title',
    opacity: '0',
    visibility: 'hidden',
    pointerEvents: 'none',
    transition: 'A opacity defaultBezier',
    zIndex: '1000',
    maxHeight: 'H',
    overflow: 'hidden auto',
    scrollBehavior: 'smooth'
  },

  // The result toast. Its key is not a component name: a key named like a
  // registered component extends it, and `Feedback` pulled the shared
  // Feedback row (a vote toggle and a heading group) into this span.
  ResultToast: {
    tag: 'span',
    position: 'absolute',
    top: '100%',
    left: '0',
    marginTop: 'Y',
    fontSize: 'Z2',
    // Adaptive (was fixed 'white') — same fix as CmdDropdown above.
    color: 'title',
    theme: 'canvas-card-glass',
    // A result toast: the capsule on the float elevation.
    padding: 'Y Z',
    round: 'radiusPill',
    shadow: 'float',
    opacity: '0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 'I',
    transition: 'B opacity defaultBezier',
    pointerEvents: 'none',
    zIndex: '1001'
  }
}
